import { useEffect } from 'react'
import Home from '@/components/pages/home'
import { useSpreadsQuery } from '@/api/hooks/use-spreads'
import { type EmbedSession, EmbedSessionProvider } from '@/contexts/embed-session-context'
import type { EmbedErrorCode } from '@/lib/embed/host-messages'
import { HOST_ACTIVITY_THROTTLE_MS } from '../constants/embed-timing'
import { useHostActivityReporter } from '../hooks/use-host-activity-reporter'
import { EmbedStatus } from './embed-status'

type EmbeddedEditorProps = {
  session: EmbedSession
  onFailure: (code: EmbedErrorCode, message: string) => void
}

/**
 * The editor in embedded mode, mounted only once the host's session has been adopted (so the
 * spreads query runs with the handed-over token). Checks the host's spread is in this book before
 * opening it, then renders the editor with the embed session in context.
 */
export function EmbeddedEditor({ session, onFailure }: EmbeddedEditorProps) {
  const { data: spreads, isError } = useSpreadsQuery()
  const hasSpread = spreads?.some((spread) => String(spread.spreadId) === session.spreadId) ?? false

  useHostActivityReporter(session.postToHost, HOST_ACTIVITY_THROTTLE_MS)

  useEffect(() => {
    if (isError) {
      onFailure('spread-not-found', "This book's spreads couldn't be loaded.")
    } else if (spreads && !hasSpread) {
      onFailure('spread-not-found', `Spread ${session.spreadId} isn't in this book.`)
    }
  }, [hasSpread, isError, onFailure, session.spreadId, spreads])

  if (!hasSpread) return <EmbedStatus message="Opening the editor…" />

  return (
    <EmbedSessionProvider value={session}>
      <Home />
    </EmbedSessionProvider>
  )
}
